/**
 * Ticket-number (correlativo) canonicalization + manual-entry validation.
 *
 * Shared so the API (Zod route schema + service duplicate check) and the web
 * "Nuevo/Editar Ticket" form validate against the SAME normalized shape — no
 * drift between client-side hints and server-side enforcement.
 */

/**
 * Canonicalize a correlativo into the single stored form.
 * "EBTC.9072638" → "EBTC-9072638"; "  ebtc.9072640 " → "EBTC-9072640".
 * Trims, strips internal whitespace, turns dots into dashes, uppercases — so the
 * same physical number typed two ways collapses to one value and the per-ámbito
 * duplicate check can't be fooled by formatting.
 */
export function normalizeCorrelativo(raw: string): string {
  return raw.trim().replace(/\s+/g, '').replace(/\./g, '-').toUpperCase();
}

/** Manual N° de ticket length bounds (checked on the NORMALIZED value). */
export const CORRELATIVO_MIN_LEN = 3;
export const CORRELATIVO_MAX_LEN = 30;

/** Shape a manual correlativo must satisfy once normalized (letters, digits, dashes). */
export const CORRELATIVO_PATTERN = /^[A-Z0-9][A-Z0-9-]*$/;

/**
 * Whether a manually-entered correlativo is acceptable, judged on its normalized
 * form (so a valid dotted/lowercase entry like "ebtc.9072638" passes). Length
 * 3-30 plus the alphanumeric/dash shape.
 */
export function isValidManualCorrelativo(raw: string): boolean {
  const n = normalizeCorrelativo(raw);
  return n.length >= CORRELATIVO_MIN_LEN && n.length <= CORRELATIVO_MAX_LEN && CORRELATIVO_PATTERN.test(n);
}
